import React, { useContext, useEffect, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import { MuiPickersAdapterContext } from '@mui/x-date-pickers'
import { makeStyles } from '@material-ui/core/styles'

export const ENTER_TIME_SELECTED_BORDER = '#0053FF'
export const ENTER_TIME_SELECTED_FILL = '#E0EAFF'
const UNSELECTED_FILL = '#F1F3F4'
const MERIDIEM_BORDER = '#DADCE0'
const TITLE_COLOR = '#5F6368'
const VALUE_COLOR = '#1C1B1F'
const LABEL_COLOR = '#5F6368'

const useStyles = makeStyles({
  root: {
    padding: '20px 24px 16px',
    minWidth: 280,
    boxSizing: 'border-box',
  },
  title: {
    margin: 0,
    marginBottom: 16,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    color: TITLE_COLOR,
  },
  fields: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    cursor: 'pointer',
  },
  digitInput: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 72,
    height: 72,
    boxSizing: 'border-box',
    borderRadius: 8,
    border: '2px solid transparent',
    backgroundColor: UNSELECTED_FILL,
    fontSize: 36,
    fontWeight: 400,
    lineHeight: 1,
    textAlign: 'center',
    color: VALUE_COLOR,
    outline: 'none',
    padding: 0,
    margin: 0,
    fontFamily: 'inherit',
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    pointerEvents: 'auto',
    position: 'relative',
    zIndex: 1,
  },
  digitInputSelected: {
    backgroundColor: `${ENTER_TIME_SELECTED_FILL} !important`,
    border: `2px solid ${ENTER_TIME_SELECTED_BORDER} !important`,
  },
  separator: {
    fontSize: 36,
    fontWeight: 500,
    color: VALUE_COLOR,
    margin: '0 8px',
    height: 72,
    lineHeight: '72px',
    userSelect: 'none',
  },
  fieldLabel: {
    marginTop: 6,
    fontSize: 12,
    lineHeight: '16px',
    color: LABEL_COLOR,
  },
  meridiem: {
    display: 'flex',
    flexDirection: 'column',
    width: 52,
    height: 72,
    marginLeft: 12,
    borderRadius: 8,
    border: `1px solid ${MERIDIEM_BORDER}`,
    overflow: 'hidden',
    alignSelf: 'flex-start',
  },
  meridiemButton: {
    flex: 1,
    border: 'none',
    backgroundColor: '#ffffff',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1,
    color: VALUE_COLOR,
    cursor: 'pointer',
    padding: 0,
    fontFamily: 'inherit',
    '&:first-child': {
      borderBottom: `1px solid ${MERIDIEM_BORDER}`,
    },
    '&.is-selected': {
      backgroundColor: ENTER_TIME_SELECTED_FILL,
    },
  },
})

export const isLuxonDateTime = value => DateTime.isDateTime(value)

export const hour24ToHour12 = hour24 => {
  const hour12 = hour24 % 12
  return hour12 === 0 ? 12 : hour12
}

export const hour12ToHour24 = (hour12, ampm) => {
  if (ampm === 'AM') {
    return hour12 === 12 ? 0 : hour12
  }
  return hour12 === 12 ? 12 : hour12 + 12
}

export const snapMinute = (minute, minutesStep) => {
  if (!minutesStep || minutesStep <= 1) {
    return minute
  }
  const snapped = Math.round(minute / minutesStep) * minutesStep
  if (snapped < 60) {
    return snapped
  }
  return 60 - minutesStep
}

const readParts = value => {
  if (isLuxonDateTime(value) && value.isValid) {
    return { hour24: value.hour, minute: value.minute }
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return { hour24: value.getHours(), minute: value.getMinutes() }
  }
  return null
}

const adapterNow = utils => {
  const now = utils?.date?.()
  if (isLuxonDateTime(now) || now instanceof Date) {
    return now
  }
  return null
}

// Empty Jr. League times are native Dates (AdapterDateFns). Empty availability
// times are Luxon. Publishing the wrong type makes date-fns format throw
// RangeError: Invalid time value.
const resolveTimeValue = (value, utils) => {
  if (readParts(value)) {
    return value
  }
  return adapterNow(utils) ?? value
}

export const applyTime = (value, hour24, minute) => {
  if (isLuxonDateTime(value)) {
    const base = value.isValid ? value : DateTime.now()
    return base.set({ hour: hour24, minute, second: 0, millisecond: 0 })
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const next = new Date(value.getTime())
    next.setHours(hour24, minute, 0, 0)
    return next
  }
  // No adapter in context (unit tests). Availability pickers resolve Luxon via
  // resolveTimeValue before this fallback.
  return DateTime.local()
    .startOf('day')
    .set({
      hour: hour24,
      minute,
      second: 0,
      millisecond: 0,
    })
}

const EnterTimeView = ({
  value,
  onChange,
  minutesStep = 1,
  disabled = false,
  readOnly = false,
  autoFocus = true,
}) => {
  const classes = useStyles()
  const adapterUtils = useContext(MuiPickersAdapterContext)?.utils ?? null
  const parts = readParts(value)
  const hour12 = parts ? hour24ToHour12(parts.hour24) : null
  const minute = parts ? parts.minute : null
  const ampm = parts && parts.hour24 >= 12 ? 'PM' : 'AM'

  const [hourDraft, setHourDraft] = useState(
    hour12 == null ? '12' : String(hour12),
  )
  const [minuteDraft, setMinuteDraft] = useState(
    minute == null ? '00' : String(minute).padStart(2, '0'),
  )
  const [hourFocused, setHourFocused] = useState(Boolean(autoFocus))
  const [minuteFocused, setMinuteFocused] = useState(false)
  const [hourFresh, setHourFresh] = useState(true)
  const [minuteFresh, setMinuteFresh] = useState(true)
  const rootRef = useRef(null)
  const latest = useRef({})
  latest.current = {
    hourFocused,
    minuteFocused,
    hourDraft,
    minuteDraft,
    hourFresh,
    minuteFresh,
    disabled,
    readOnly,
    hour12,
    minute,
    ampm,
    value,
    minutesStep,
    onChange,
    utils: adapterUtils,
  }

  useEffect(() => {
    if (!hourFocused) {
      setHourDraft(hour12 == null ? '12' : String(hour12))
    }
  }, [hour12, hourFocused])

  useEffect(() => {
    if (!minuteFocused) {
      setMinuteDraft(minute == null ? '00' : String(minute).padStart(2, '0'))
    }
  }, [minute, minuteFocused])

  const commitFrom = (state, next) => {
    const nextHour12 = next.nextHour12 ?? state.hour12 ?? 12
    const nextMinute = next.nextMinute ?? state.minute ?? 0
    const nextAmPm = next.nextAmPm ?? state.ampm
    if (state.disabled || state.readOnly) {
      return
    }
    state.onChange?.(
      applyTime(
        resolveTimeValue(state.value, state.utils),
        hour12ToHour24(nextHour12, nextAmPm),
        nextMinute,
      ),
    )
  }

  const flushHourDraft = state => {
    const parsed = parseInt(state.hourDraft, 10)
    if (Number.isNaN(parsed) || parsed < 1 || parsed > 12) {
      return
    }
    commitFrom(state, { nextHour12: parsed })
  }

  const applyHourDigits = (state, raw) => {
    setHourDraft(raw)
    if (raw === '') {
      return
    }
    const parsed = parseInt(raw, 10)
    if (parsed < 1 || parsed > 12) {
      return
    }
    if (raw.length === 2 || parsed >= 3) {
      setHourFocused(false)
      setMinuteFocused(true)
      setMinuteFresh(true)
      commitFrom(state, { nextHour12: parsed })
    }
  }

  const applyMinuteDigits = (state, raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 2)
    setMinuteDraft(digits)
    if (digits === '') {
      return
    }
    const parsed = parseInt(digits, 10)
    if (parsed > 59) {
      return
    }
    if (digits.length < 2) {
      return
    }
    commitFrom(state, {
      nextMinute: snapMinute(parsed, state.minutesStep),
    })
  }

  const selectHour = event => {
    event.stopPropagation()
    if (disabled || readOnly) {
      return
    }
    event.currentTarget.focus()
    setHourFocused(true)
    setMinuteFocused(false)
    setHourDraft('')
    setHourFresh(true)
  }

  const selectMinute = event => {
    event.stopPropagation()
    if (disabled || readOnly) {
      return
    }
    event.currentTarget.focus()
    if (hourFocused) {
      flushHourDraft(latest.current)
    }
    setHourFocused(false)
    setMinuteFocused(true)
    setMinuteDraft('')
    setMinuteFresh(true)
  }

  useEffect(() => {
    const onKeyDown = event => {
      const state = latest.current
      const target = event.target
      const isEditableField =
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      if (isEditableField && !rootRef.current?.contains(target)) {
        return
      }
      if (
        state.disabled ||
        state.readOnly ||
        (!state.hourFocused && !state.minuteFocused)
      ) {
        return
      }
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return
      }

      const isDigit = /^[0-9]$/.test(event.key)
      const isBackspace = event.key === 'Backspace'
      if (!isDigit && !isBackspace) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      if (state.hourFocused) {
        if (isBackspace) {
          setHourFresh(false)
          applyHourDigits(state, state.hourDraft.slice(0, -1))
          return
        }
        const raw = (state.hourFresh
          ? event.key
          : `${state.hourDraft}${event.key}`
        )
          .replace(/\D/g, '')
          .slice(0, 2)
        setHourFresh(false)
        applyHourDigits(state, raw)
        return
      }

      if (isBackspace) {
        setMinuteFresh(false)
        applyMinuteDigits(
          state,
          state.minuteDraft.replace(/^0/, '').slice(0, -1),
        )
        return
      }
      const raw = (state.minuteFresh
        ? event.key
        : `${state.minuteDraft}${event.key}`
      )
        .replace(/\D/g, '')
        .slice(0, 2)
      setMinuteFresh(false)
      applyMinuteDigits(state, raw)
    }

    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key handler reads latest.current
  }, [])

  const commit = next => commitFrom(latest.current, next)

  const hourInputClassName = `${classes.digitInput}${
    hourFocused ? ` ${classes.digitInputSelected}` : ''
  }`
  const minuteInputClassName = `${classes.digitInput}${
    minuteFocused ? ` ${classes.digitInputSelected}` : ''
  }`
  const hourDisplay = hourFocused
    ? hourDraft
    : hour12 == null
      ? '12'
      : String(hour12)
  const minuteDisplay = minuteFocused
    ? minuteDraft
    : minute == null
      ? '00'
      : String(minute).padStart(2, '0')

  return (
    <div ref={rootRef} className={classes.root} data-testid="enter-time-view">
      <p className={classes.title}>Enter time</p>
      <div className={classes.fields}>
        <div className={classes.fieldGroup}>
          <button
            type="button"
            aria-label="Hour"
            aria-pressed={hourFocused}
            className={hourInputClassName}
            disabled={disabled || readOnly}
            onClick={selectHour}
          >
            {hourDisplay}
          </button>
          <span className={classes.fieldLabel}>Hour</span>
        </div>
        <span className={classes.separator} aria-hidden="true">
          :
        </span>
        <div className={classes.fieldGroup}>
          <button
            type="button"
            aria-label="Minute"
            aria-pressed={minuteFocused}
            className={minuteInputClassName}
            disabled={disabled || readOnly}
            onClick={selectMinute}
          >
            {minuteDisplay}
          </button>
          <span className={classes.fieldLabel}>Minute</span>
        </div>
        <div
          className={classes.meridiem}
          role="radiogroup"
          aria-label="AM or PM"
        >
          {['AM', 'PM'].map(option => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={ampm === option}
              className={`${classes.meridiemButton}${
                ampm === option ? ' is-selected' : ''
              }`}
              disabled={disabled || readOnly}
              onMouseDown={event => {
                event.preventDefault()
                event.stopPropagation()
              }}
              onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                commit({ nextAmPm: option })
              }}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export const renderEnterTimeView = ({
  value,
  onChange,
  minutesStep,
  disabled,
  readOnly,
  autoFocus = true,
}) => (
  <EnterTimeView
    value={value}
    minutesStep={minutesStep}
    disabled={disabled}
    readOnly={readOnly}
    autoFocus={autoFocus !== false}
    onChange={nextValue => onChange(nextValue, 'partial')}
  />
)

export const enterTimeViewRenderers = {
  hours: renderEnterTimeView,
}

export const ENTER_TIME_FIELD_FORMAT = 'hh:mm a'

export const enterTimePickerPopperSx = {
  zIndex: 1500,
  '& .MuiPaper-root': {
    minWidth: 320,
    borderRadius: '16px',
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
    overflow: 'visible',
    border: '1px solid #E8EAED',
  },
  '& .MuiPickersLayout-root': {
    display: 'block',
  },
  '& .MuiPickersLayout-contentWrapper': {
    overflow: 'visible',
    pointerEvents: 'auto',
  },
  '& .MuiMultiSectionDigitalClock-root, & .MuiDigitalClock-root, & .MuiTimeClock-root': {
    display: 'none',
  },
  '& [data-testid="enter-time-view"]': {
    position: 'relative',
    zIndex: 2,
    pointerEvents: 'auto',
  },
  '& .MuiDialogActions-root, & .MuiPickersLayout-actionBar': {
    display: 'none',
  },
}

export const withEnterTimePicker = (slotProps = {}) => ({
  closeOnSelect: false,
  views: ['hours'],
  format: ENTER_TIME_FIELD_FORMAT,
  viewRenderers: enterTimeViewRenderers,
  slotProps: mergeEnterTimePickerSlotProps(slotProps),
})

export const mergeEnterTimePickerSlotProps = (slotProps = {}) => ({
  ...slotProps,
  toolbar: {
    hidden: true,
    ...slotProps.toolbar,
  },
  actionBar: {
    ...slotProps.actionBar,
    actions: [],
  },
  layout: {
    ...slotProps.layout,
    sx: {
      '& .MuiPickersLayout-actionBar': { display: 'none' },
      '& .MuiDialogActions-root': { display: 'none' },
      ...(slotProps.layout?.sx || {}),
    },
  },
  popper: {
    disablePortal: false,
    ...slotProps.popper,
    sx: {
      ...enterTimePickerPopperSx,
      ...(slotProps.popper?.sx || {}),
    },
  },
})

export default EnterTimeView
